import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiGet, apiPatch } from '../../api/client';
import { channelLabel } from '../console/conversationChannelMeta';
import { DEMO_LEVEL_COLOR, DEMO_LEVEL_LABEL, formatDemoDate } from './demoUsageUi';
import type { SuperOrgDetailOutletContext } from './SuperOrgDetailLayout';
import { superOrgBtn, superOrgField, superOrgInput } from './superOrgPanels';
import type { DemoOrgUsageDetail } from './superOrgTypes';

function Kpi({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 10,
        padding: 14,
        minWidth: 120,
        flex: '1 1 120px',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 4 }}>{value}</div>
      {hint ? <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{hint}</div> : null}
    </div>
  );
}

export function SuperOrgDemoUsagePage() {
  const { org, token, reload, setPageErr } = useOutletContext<SuperOrgDetailOutletContext>();
  const [detail, setDetail] = useState<DemoOrgUsageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState(org.demoNotes ?? '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [marking, setMarking] = useState(false);

  useEffect(() => {
    setNotes(org.demoNotes ?? '');
  }, [org.demoNotes]);

  const load = useCallback(async () => {
    setLoading(true);
    setPageErr('');
    try {
      const d = await apiGet<DemoOrgUsageDetail>(`/api/super/organizations/${org.id}/demo-usage`, token);
      setDetail(d);
    } catch (e) {
      setPageErr((e as Error).message);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [org.id, token, setPageErr]);

  useEffect(() => {
    void load();
  }, [load]);

  const markAsDemo = async () => {
    setMarking(true);
    setPageErr('');
    try {
      await apiPatch(`/api/super/organizations/${org.id}`, { isDemo: true }, token, {
        successMessage: 'Marcada como empresa de demo',
      });
      await reload();
    } catch (e) {
      setPageErr((e as Error).message);
    } finally {
      setMarking(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    setPageErr('');
    try {
      await apiPatch(`/api/super/organizations/${org.id}`, { demoNotes: notes }, token, {
        successMessage: 'Notas guardadas',
      });
      await reload();
    } catch (e) {
      setPageErr((e as Error).message);
    } finally {
      setSavingNotes(false);
    }
  };

  if (!org.isDemo) {
    return (
      <div>
        <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Uso demo</h2>
        <p style={{ color: '#64748b', fontSize: 14 }}>
          Esta empresa no está marcada como demo. Marcála para incluirla en el análisis comercial de pruebas.
        </p>
        <button type="button" style={superOrgBtn} disabled={marking} onClick={() => void markAsDemo()}>
          {marking ? 'Guardando…' : 'Marcar como empresa de demo'}
        </button>
      </div>
    );
  }

  const level = detail?.level;
  const colors = level ? DEMO_LEVEL_COLOR[level] : null;

  return (
    <div>
      <h2 style={{ fontSize: 16, margin: '0 0 8px' }}>Uso demo</h2>
      {loading && !detail ? <p style={{ color: '#64748b' }}>Cargando análisis…</p> : null}

      {detail && colors ?
        <>
          <div
            style={{
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: colors.fg, textTransform: 'uppercase' }}>
              {DEMO_LEVEL_LABEL[detail.level]}
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 15, color: '#0f172a', fontWeight: 600 }}>{detail.action}</p>
            <p style={{ margin: '8px 0 0', fontSize: 13, color: '#475569' }}>
              Alta hace {detail.daysSinceCreated} días · último visitante:{' '}
              {formatDemoDate(detail.lastVisitorMessageAt ?? detail.lastConversationAt)}
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
            <Kpi label="Conversaciones" value={detail.conversations.total} hint={`${detail.conversations.open} abiertas`} />
            <Kpi label="Actualizadas 7d" value={detail.conversations.updatedLast7d} hint={`${detail.conversations.updatedLast30d} en 30d`} />
            <Kpi label="Msgs visitante 7d" value={detail.messages.visitorLast7d} hint={`${detail.messages.visitorTotal} total`} />
            <Kpi label="Msgs agente" value={detail.messages.agentTotal} hint={`${detail.messages.last7d} msgs en 7d`} />
            <Kpi label="Modo humano" value={detail.conversations.humanOpen} hint="hilos abiertos" />
            <Kpi
              label="Consumo 30d"
              value={
                Number.isFinite(detail.estimatedUsdLast30d)
                  ? `US$ ${detail.estimatedUsdLast30d.toFixed(2)}`
                  : '—'
              }
            />
            <Kpi label="Usuarios" value={detail.users} hint={`${detail.agents} agentes`} />
          </div>

          <section style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Canales</h3>
            {detail.channels.length === 0 ?
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Todavía no hay conversaciones.</p>
            : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {detail.channels.map((ch) => (
                  <li
                    key={ch.channel}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 0',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 13,
                    }}
                  >
                    <span>
                      {channelLabel(ch.channel)}
                      {ch.channel !== 'demo' && ch.channel !== 'testchat' ?
                        <span style={{ marginLeft: 8, color: '#047857', fontWeight: 600, fontSize: 11 }}>canal real</span>
                      : <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 11 }}>sandbox</span>}
                    </span>
                    <span style={{ color: '#64748b' }}>
                      {ch.conversations} hilos · {ch.messages} msgs
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {detail.connectedIntegrations.length > 0 ?
              <p style={{ fontSize: 12, color: '#047857', marginTop: 10 }}>
                Integraciones conectadas:{' '}
                {detail.connectedIntegrations.map((i) => i.provider).join(', ')}
              </p>
            : (
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10 }}>Sin integraciones conectadas.</p>
            )}
          </section>

          <section style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Mensajes por día (30d)</h3>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 72, overflowX: 'auto' }}>
              {detail.messagesByDay.map((d) => {
                const max = Math.max(1, ...detail.messagesByDay.map((x) => x.visitor + x.agent));
                const h = Math.round(((d.visitor + d.agent) / max) * 64);
                return (
                  <div
                    key={d.date}
                    title={`${d.date}: ${d.visitor} vis. / ${d.agent} ag.`}
                    style={{
                      width: 8,
                      height: Math.max(2, h),
                      background: d.visitor + d.agent > 0 ? '#7c3aed' : '#e2e8f0',
                      borderRadius: 2,
                      flexShrink: 0,
                    }}
                  />
                );
              })}
            </div>
          </section>

          <section style={{ marginBottom: 18 }}>
            <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Hilos recientes</h3>
            {detail.recentThreads.length === 0 ?
              <p style={{ color: '#94a3b8', fontSize: 13 }}>Sin conversaciones.</p>
            : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {detail.recentThreads.map((t) => (
                  <li
                    key={t.id}
                    style={{
                      padding: '10px 0',
                      borderBottom: '1px solid #f1f5f9',
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>{t.visitorName?.trim() || t.id.slice(0, 8)}</div>
                    <div style={{ color: '#64748b', marginTop: 2 }}>
                      {channelLabel(t.channel)} · {t.messageCount} msgs · {t.controlMode} · {t.status} ·{' '}
                      {formatDemoDate(t.updatedAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      : null}

      <section>
        <h3 style={{ fontSize: 14, margin: '0 0 8px' }}>Notas comerciales</h3>
        <label style={{ ...superOrgField, width: '100%' }}>
          Contexto interno (contacto, objeciones, próximo paso)
          <textarea
            style={{ ...superOrgInput, minHeight: 90, resize: 'vertical' }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={4000}
          />
        </label>
        <button type="button" style={{ ...superOrgBtn, marginTop: 8 }} disabled={savingNotes} onClick={() => void saveNotes()}>
          {savingNotes ? 'Guardando…' : 'Guardar notas'}
        </button>
      </section>
    </div>
  );
}
